(() => {
  const HEADER_SELECTOR = '#header-component[data-menu-style="menu"]';
  const DESKTOP_QUERY = '(min-width: 990px)';

  let scheduled = false;
  let menuObserver = null;
  let observedMenu = null;
  let headerRootObserver = null;
  let observedHeaderRoot = null;

  const schedule = () => {
    if (scheduled) return;
    scheduled = true;

    requestAnimationFrame(() => {
      scheduled = false;
      applyHeaderBalance();
    });
  };

  const getRegularItems = (menu) =>
    Array.from(menu.children).filter(
      (item) =>
        item.classList?.contains('menu-list__list-item') &&
        item.getAttribute('slot') !== 'more'
    );

  const fallbackWidths = (items) =>
    items.map((item) => {
      const text =
        item.querySelector('.menu-list__link-title')?.textContent?.trim() ||
        item.textContent?.trim() ||
        '';
      return text.length + 6;
    });

  const getNaturalWidths = (items) => {
    const sampleItem =
      items.find((item) => item.getAttribute('slot') !== 'overflow') || items[0];
    const sampleLink = sampleItem?.querySelector('.menu-list__link');
    const sampleTitle = sampleItem?.querySelector('.menu-list__link-title');

    if (!sampleLink || !sampleTitle) return fallbackWidths(items);

    const titleStyle = getComputedStyle(sampleTitle);
    const linkStyle = getComputedStyle(sampleLink);
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');

    if (!context) return fallbackWidths(items);

    context.font = [
      titleStyle.fontStyle,
      titleStyle.fontVariant,
      titleStyle.fontWeight,
      titleStyle.fontSize,
      titleStyle.fontFamily,
    ].join(' ');

    const linkPadding =
      (parseFloat(linkStyle.paddingInlineStart) || 0) +
      (parseFloat(linkStyle.paddingInlineEnd) || 0);

    return items.map((item) => {
      const text =
        item.querySelector('.menu-list__link-title')?.textContent?.trim() ||
        item.textContent?.trim() ||
        '';
      return context.measureText(text).width + linkPadding;
    });
  };

  const chooseSplitIndex = (items) => {
    if (items.length < 2) return -1;

    const widths = getNaturalWidths(items);
    const total = widths.reduce((sum, width) => sum + width, 0);
    let left = 0;
    let bestIndex = 1;
    let bestDelta = Number.POSITIVE_INFINITY;

    for (let index = 1; index < widths.length; index += 1) {
      left += widths[index - 1];
      const right = total - left;
      const delta = Math.abs(left - right);

      if (delta < bestDelta) {
        bestDelta = delta;
        bestIndex = index;
      }
    }

    return bestIndex;
  };

  const cleanup = (header, menu) => {
    menu
      ?.querySelectorAll('[data-tg-logo-split]')
      .forEach((item) => item.removeAttribute('data-tg-logo-split'));
    header?.style.removeProperty('--tg-header-logo-reserve');
  };

  const observeMenu = (menu) => {
    if (observedMenu === menu) return;

    menuObserver?.disconnect();
    menuObserver = new MutationObserver(schedule);
    menuObserver.observe(menu, { childList: true });
    observedMenu = menu;
  };

  const observeHeaderRoot = () => {
    const root = document.getElementById('header-group') || document.body;
    if (!root || observedHeaderRoot === root) return;

    headerRootObserver?.disconnect();
    headerRootObserver = new MutationObserver(schedule);
    headerRootObserver.observe(root, { childList: true, subtree: true });
    observedHeaderRoot = root;
  };

  function applyHeaderBalance() {
    observeHeaderRoot();

    const header = document.querySelector(HEADER_SELECTOR);
    const menu = header?.querySelector('overflow-list.overflow-menu');

    if (!header || !menu) {
      if (observedMenu && !observedMenu.isConnected) {
        menuObserver?.disconnect();
        observedMenu = null;
      }
      return;
    }

    if (!window.matchMedia(DESKTOP_QUERY).matches) {
      cleanup(header, menu);
      return;
    }

    observeMenu(menu);

    const items = getRegularItems(menu);
    const logo = header.querySelector('.header-logo');

    if (items.length < 2 || !logo) {
      cleanup(header, menu);
      return;
    }

    const splitIndex = chooseSplitIndex(items);
    const splitItem = items[splitIndex];
    const logoWidth = logo.getBoundingClientRect().width;

    if (!splitItem || !logoWidth) return;

    const safety = Math.min(26, Math.max(18, Math.round(logoWidth * 0.14)));
    const reserve = Math.ceil(logoWidth + safety * 2);
    const previousReserve =
      parseFloat(header.style.getPropertyValue('--tg-header-logo-reserve')) || 0;
    const previousSplit = menu.querySelector('[data-tg-logo-split]');
    const splitChanged = previousSplit !== splitItem;
    const reserveChanged = Math.abs(previousReserve - reserve) > 1;

    if (splitChanged) {
      previousSplit?.removeAttribute('data-tg-logo-split');
      splitItem.setAttribute('data-tg-logo-split', '');
    }

    header.style.setProperty('--tg-header-logo-reserve', `${reserve}px`);

    if (splitChanged || reserveChanged) {
      menu.dispatchEvent(new CustomEvent('reflow'));
    }
  }

  window.addEventListener('resize', schedule, { passive: true });
  window.addEventListener('load', schedule, { once: true });
  document.addEventListener('shopify:section:load', () => {
    observeHeaderRoot();
    schedule();
  });
  document.fonts?.ready?.then(schedule);

  observeHeaderRoot();
  schedule();
})();
